/**
 * Homepage "Every Department. Every Step. One Source of Truth." section.
 *
 * A 10-step horizontal workflow with outlined color-coded icon circles and
 * connecting arrows, followed by a 5-item benefit strip below.
 *
 * Content is admin-editable via Site Settings -> Homepage -> "Features" tab.
 * Icons and per-step colours are position-locked to slots.
 */
import { motion } from "framer-motion";
import {
  Users, MonitorPlay, FileText, ShoppingCart, Palette, Factory, Truck,
  Receipt, Landmark, Sparkles,
  ChevronRight,
  Database, RefreshCw, ShieldCheck, Clock, BarChart3,
} from "lucide-react";
import { useSiteSettings } from "@/data/useSiteSettings";
import { FEATURES_GRID_DEFAULT, mergeSection } from "@/data/homepageSectionDefaults";

/* ---- Position-locked icons + colours ---- */

// Uses hex text-colour classes so the outlined circle + label share the same accent.
const STEP_META = [
  { Icon: Users,       text: "text-blue-600",    border: "border-blue-500/60"    },
  { Icon: MonitorPlay, text: "text-violet-600",  border: "border-violet-500/60"  },
  { Icon: FileText,    text: "text-teal-600",    border: "border-teal-500/60"    },
  { Icon: ShoppingCart,text: "text-orange-500",  border: "border-orange-400/60"  },
  { Icon: Palette,     text: "text-red-500",     border: "border-red-400/60"     },
  { Icon: Factory,     text: "text-green-600",   border: "border-green-500/60"   },
  { Icon: Truck,       text: "text-cyan-600",    border: "border-cyan-500/60"    },
  { Icon: Receipt,     text: "text-blue-500",    border: "border-blue-400/60"    },
  { Icon: Landmark,    text: "text-purple-600",  border: "border-purple-500/60"  },
  { Icon: Sparkles,    text: "text-fuchsia-600", border: "border-fuchsia-500/60" },
];

const BENEFIT_META = [
  { Icon: Database    },
  { Icon: RefreshCw   },
  { Icon: ShieldCheck },
  { Icon: Clock       },
  { Icon: BarChart3   },
];

/* ---- Component ---- */

export default function FeaturesGrid({ data }) {
  const settings = useSiteSettings();
  const merged = mergeSection(FEATURES_GRID_DEFAULT, settings?.homepage?.features_grid);
  const c = mergeSection(merged, data);

  const steps =
    Array.isArray(c.steps) && c.steps.length > 0
      ? c.steps.slice(0, STEP_META.length)
      : FEATURES_GRID_DEFAULT.steps;
  const benefits =
    Array.isArray(c.benefits) && c.benefits.length > 0
      ? c.benefits.slice(0, BENEFIT_META.length)
      : FEATURES_GRID_DEFAULT.benefits;

  return (
    <section data-testid="features-grid-section" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-4xl mx-auto mb-10 lg:mb-14"
        >
          {c.eyebrow && (
            <span
              data-testid="features-grid-eyebrow"
              className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-sky-600"
            >
              {c.eyebrow}
            </span>
          )}
          <h2
            data-testid="features-grid-heading"
            className="mt-3 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]"
          >
            {c.heading}
          </h2>
          <p className="mt-5 text-base lg:text-lg text-slate-500 leading-relaxed max-w-2xl mx-auto">
            {c.intro}
          </p>
        </motion.div>

        {/* 10-step workflow row */}
        <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-x-2 lg:gap-x-1 gap-y-8">
          {steps.map((step, i) => {
            const meta = STEP_META[i] || STEP_META[STEP_META.length - 1];
            const Icon = meta.Icon;
            const isLast = i === steps.length - 1;
            const slug = (step.title || `step-${i}`)
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)/g, "");
            return (
              <motion.div
                key={`${slug}-${i}`}
                data-testid={`features-step-${slug}`}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.05, duration: 0.4 }}
                className="relative text-center"
              >
                {/* Outlined icon circle */}
                <span
                  aria-hidden
                  className={`inline-flex w-14 h-14 lg:w-16 lg:h-16 rounded-full border-2 bg-white items-center justify-center mb-3 ${meta.border}`}
                >
                  <Icon className={`h-6 w-6 lg:h-7 lg:w-7 ${meta.text}`} strokeWidth={1.75} />
                </span>

                {/* Coloured label */}
                <h3 className={`text-[13px] lg:text-sm font-bold tracking-wider uppercase ${meta.text} leading-tight`}>
                  {step.title}
                </h3>

                {/* Description */}
                <p className="mt-1.5 text-[11px] lg:text-[12px] text-slate-500 leading-snug px-1 max-w-[10rem] mx-auto">
                  {step.desc}
                </p>

                {/* Chevron arrow between cards (desktop only, not after the last) */}
                {!isLast && (
                  <span
                    aria-hidden
                    className="hidden lg:flex absolute top-6 lg:top-7 -right-1.5 w-3 h-3 items-center justify-center"
                  >
                    <ChevronRight className="h-4 w-4 text-slate-400" strokeWidth={2} />
                  </span>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Benefit strip - 5 items in a light-blue panel */}
        <div className="mt-12 lg:mt-14 rounded-2xl bg-blue-50/50 border border-blue-100/70 p-5 sm:p-6 lg:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-4">
            {benefits.map((b, i) => {
              const meta = BENEFIT_META[i] || BENEFIT_META[BENEFIT_META.length - 1];
              const Icon = meta.Icon;
              const slug = (b.title || `benefit-${i}`)
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "");
              return (
                <div
                  key={`${slug}-${i}`}
                  data-testid={`features-benefit-${slug}`}
                  className="flex items-start gap-3"
                >
                  <span className="inline-flex w-11 h-11 shrink-0 rounded-xl bg-white ring-1 ring-blue-100 items-center justify-center">
                    <Icon className="h-5 w-5 text-blue-500" strokeWidth={2} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-slate-900 mb-1">{b.title}</h3>
                    <p className="text-[13px] text-slate-500 leading-snug">{b.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
